import { Collisions, GameRandom, Inject, ModelEvents, Singleton } from '@denishussar/html5-game-engine';
import { CarModel } from './CarModel';
import { Random } from '../Utils/Math';
import { GameScreenController } from '../Controllers/Game/GameScreenController';
import { IBackendGameState } from '../Backend';
import { getCommonConfig } from '../Common/GameConfig';
import { GeneratorModel } from './GeneratorModel';

export enum AppStateEnum {
    none,
    name,
    menu,
    game,
    endscreen,
    garage,
    levelSelector,
    leaderboard,
    leaderboardFromGarage,
}

export enum PlayerRoadIndexEnum {
    center = 0,
    left = -1,
    right = 1,
}

export enum GamePlayerTypeEnum {
    none = 'none',
    vaz = 'vaz',
    delorean = 'delorean',
    ferrari = 'ferrari',
    lamborgini = 'lamborgini',
}

export enum GameObstacleTypeEnum {
    none = 'none',
    hole = 'hole',
    stone = 'stone',
    horse = 'horse',

    //special
    roadSign = 'road_sign',

    //decorations
    hotel = 'hotel',   // decor signs
    flora = 'flora',  // decor flora


}

export enum GameMoveableObstacleTypeEnum {
    none = 'none',
    gelandewagen = 'gelandewagen',
    police = 'police',
    policeCrown = 'policeCrown',
    degen = 'degen',
    golf = 'golf',
    hummer = 'hummer',
}

export enum GameCollectableTypeEnum {
    none = 'none',
    horse = 'horse',
    bat = 'bat',
    crypto = 'crypto',
    gun = 'gun',
}

export enum GameObstacleGroupType {
    none = 'none',
    simple = 'simple',
    medium = 'medium',
    hard = 'hard'
}

export enum GenObstacleEnum {
    low,
    med,
    high,
}

export interface IGenObstacle {
    type: GenObstacleEnum,
    distance: number,
    road: number,
    obstacleType: GameObstacleTypeEnum | GameMoveableObstacleTypeEnum,
}

export enum RoadObjectType {
    obstacle,
    movableObstacle,
    collectable,
    gasStation,
}

@Singleton
export class GameModel {
    public ftue = 1;
    public gameScreen: GameScreenController = null;
    public gameScreen_ObstacleCounter =0;
    public gameScreen_ObstacleProjectionCounter =0;
    public gameScreen_MoveableObstacleCounter =0;
    public gameScreen_MoveableObstacleProjectionCounter =0;
    public pause: boolean = false;

    public generator = new GeneratorModel();

    public highresTimer = 0.31;
    public lowresTimer = 0.21;
    public highresCounter = 0;
    public lowresCounter = 0;

    public static SCALE: number = 0.65;

    public spineResources = {};

    public startTimestamp: number = 0;

    //garage state
    public garage_selectedCar = 0;
    public garage_maxCar = 4;
    public garage_selectedUpgrade = 0;
    public garage_maxUpgrades = 3;
    public garage_selectedPaint = 0;
    public garage_maxPaint = 4;
    public garage_currentTab = 0;

    public gameWidth: number = 1080;
    public gameHeight: number = 1920;

    public appState: AppStateEnum = AppStateEnum.none;
    public showFullscreen: boolean = false;

    public playerName: string = '';
    public coins: number = 60000;// 1000000;
    public levelCoins: number = 0;

    public currentPlayer: CarModel | null = null;
    public cars: CarModel[] = new Array<CarModel>(this.garage_maxCar);
    public currentLevel: number = 1;

    public currentTime: number = 0;
    public currentGasInitial: number = 3;
    public currentGas: number = this.currentGasInitial;

    // public gasStationDistance: number = 0;
    public playerDistance: number = 0;

    // public floraDistance: number = 0;
    // public floraDistanceMax: number = 100;

    // public signDistance: number = 0;
    // public signDistanceNext: number = 30;
    // public signDistanceMax: number = 500;

    public horseSpeedUpTime: number = 0;
    public horseSpeedUpTimeMax: number = 5;
    public horseSpeedUpPercent: number = 1.25;

    // public previousObstacleType: GameMoveableObstacleTypeEnum | GameObstacleTypeEnum = GameObstacleTypeEnum.none;
    // public previousCollectableType: GameCollectableTypeEnum = GameCollectableTypeEnum.none;

    // public previousRoadIndex: number = undefined;
    // public previousHorseRoadIndex: number = undefined;

    public gasStationCheckedCount: number = 0;

    public updatePerSecondTime: number = 0;

    public playerRoadIndex: PlayerRoadIndexEnum = PlayerRoadIndexEnum.center;
    public playerType: GamePlayerTypeEnum = GamePlayerTypeEnum.vaz;

    public collectablesState: Map<string, boolean> = new Map();
    public collectablesObstacle = {};

    constructor(
        @Inject private _$random: GameRandom,
        @Inject private _$modelEvents: ModelEvents,
        @Inject private _$collisions: Collisions,
    ) {

        Object.values(GameCollectableTypeEnum).forEach((key) => {
            this.collectablesState[key] = false;
            this.collectablesObstacle[key] = 0;
        });

        for (let i = 0; i < this.cars.length; i++) {
            this.cars[i] = new CarModel(i);
        }

        this.currentPlayer = this.cars[0];
        this.cars[0].isBought = true;
    }

    public loadFromBackend(state: IBackendGameState): void {
        this.playerName = state.name;
        this.coins = state.coins;

        this.cars.forEach((car, index) => {
            const backendCar = state.cars[index];

            car.health = backendCar.health;
            car.isBought = !backendCar.locked;
            car.paint = backendCar.color;
            car.availablePaints = backendCar.unlockedColors ? backendCar.unlockedColors : [true, false, false, false];
            car.upgrades = backendCar.upgrades ? backendCar.upgrades : [0, 0, 0, 0];
        });

        this.currentPlayer = this.cars.find(car => car.index == state.currentCar);
        this.garage_selectedCar = this.currentPlayer.index;
        this.garage_selectedPaint = this.currentPlayer.paint;
        this.garage_selectedUpgrade = 0;
    }

    public resetLevel() {
        // this.gameScreen = null;
        this.playerRoadIndex = 0;
        this.cars[0].health = 100;
        this.levelCoins = 0;
        this.currentTime = 0;
        this.currentGas = this.currentGasInitial;
        this.lowresCounter= 0.001;
        this.highresCounter=0.001;
        this.playerDistance = 0;

        this.horseSpeedUpTime = 0;

        this.gasStationCheckedCount = 0;
        this.updatePerSecondTime = 0;

        Object.values(GameCollectableTypeEnum).forEach((type) => this.collectablesState[type] = false);
        this.cars.forEach((car) => car.reset());


        this.generator.reset();
    }

    public fillPlayer(config: any): void {
         this.currentPlayer.gasRate = config.gasRate;
         this.currentPlayer.speedMax = config.speed;
         this.currentPlayer.speedAcceleration = config.acceleration;
     }

    public get playerGasRate(): number {
        return this.currentPlayer.gasRate;
    }

    public set playerSpeed(value: number) {
        this.currentPlayer.speed = value;
    }

    public get playerSpeed(): number {
        return this.currentPlayer.speed;
    }

    public set playerHealth(value: number) {
        this.currentPlayer.health = value;
    }

    public get playerHealth(): number {
        return this.currentPlayer.health;
    }

    public get playerSpeedAcceleration(): number {
        return this.currentPlayer.speedAcceleration;
    }

    public get playerSpeedMax(): number {
        return this.currentPlayer.speedMax;
    }

    public get playerSpeedFriction(): number {
        return this.currentPlayer.speedFriction;
    }

    public get playerBrakeSpeed(): number {
        return this.currentPlayer.brakeSpeed;
    }
}
